import React from 'react';
import { Tag, Typography } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';

const { Text } = Typography;

export const getRequestEndTime = (app) => (
  app?.expiresAt || app?.expires_at || app?.endTime || app?.end_time || app?.endDateTime || app?.end_date_time
);

const getTimeRemainingParts = (endTime, now) => {
  const endTimestamp = new Date(endTime).getTime();
  if (!endTime || Number.isNaN(endTimestamp)) return null;

  const totalSeconds = Math.max(0, Math.floor((endTimestamp - now) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return {
    minutes: String(minutes).padStart(2, '0'),
    seconds: String(seconds).padStart(2, '0'),
    totalSeconds,
  };
};

const formatEndTime = (endTime) => {
  const endDate = new Date(endTime);
  if (!endTime || Number.isNaN(endDate.getTime())) return null;

  return endDate.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const QuickActionTimer = ({ app, now, style }) => {
  const endTime = getRequestEndTime(app);
  const timeRemaining = getTimeRemainingParts(endTime, now);
  const endTimeLabel = formatEndTime(endTime);
  const hasEnded = timeRemaining?.totalSeconds === 0;
  const urgencyPercent = timeRemaining
    ? Math.max(8, Math.min(100, (timeRemaining.totalSeconds / 3600) * 100))
    : 0;

  if (!timeRemaining) return null;

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #ffccc7',
      borderRadius: '8px',
      boxShadow: '0 6px 16px rgba(207, 19, 34, 0.08)',
      overflow: 'hidden',
      width: '100%',
      ...style
    }}>
      <div style={{ padding: '10px 12px 12px' }}>
        <div style={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          gap: '10px',
          marginBottom: '10px'
        }}>
          <div style={{ alignItems: 'center', display: 'flex', gap: '8px', minWidth: 0 }}>
            <span style={{
              alignItems: 'center',
              background: '#cf1322',
              borderRadius: '6px',
              color: '#fff',
              display: 'flex',
              flexShrink: 0,
              height: '26px',
              justifyContent: 'center',
              width: '26px'
            }}>
              <ClockCircleOutlined />
            </span>
            <div style={{ minWidth: 0 }}>
              <Text style={{
                color: hasEnded ? '#cf1322' : '#262626',
                display: 'block',
                fontSize: '14px',
                fontWeight: 800,
                lineHeight: 1.1
              }}>
                {hasEnded ? 'Bid ended' : 'Bid closes'}
              </Text>
              {endTimeLabel && (
                <Text type="secondary" style={{ display: 'block', fontSize: '11px', lineHeight: 1.2 }}>
                  {hasEnded ? 'Ended at' : 'Today at'} {endTimeLabel}
                </Text>
              )}
            </div>
          </div>
          <Tag color="error" bordered={false} style={{ margin: 0, fontSize: '11px', fontWeight: 700 }}>
            {hasEnded ? 'ENDED' : 'LIVE'}
          </Tag>
        </div>
        {hasEnded ? (
          <div style={{
            background: '#fff1f0',
            border: '1px solid #ffccc7',
            borderRadius: '6px',
            color: '#cf1322',
            fontSize: '15px',
            fontWeight: 900,
            padding: '10px 12px',
            textAlign: 'center',
            textTransform: 'uppercase'
          }}>
            Bid ended
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gap: '6px',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
          }}>
            {[
              { label: 'Min', value: timeRemaining.minutes },
              { label: 'Sec', value: timeRemaining.seconds },
            ].map((unit) => (
              <div
                key={unit.label}
                style={{
                  background: '#fff1f0',
                  border: '1px solid #ffccc7',
                  borderRadius: '6px',
                  minWidth: 0,
                  padding: '7px 4px',
                  textAlign: 'center'
                }}
              >
                <div style={{
                  color: '#cf1322',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                  fontSize: '18px',
                  fontWeight: 900,
                  lineHeight: 1
                }}>
                  {unit.value}
                </div>
                <div style={{
                  color: '#8c8c8c',
                  fontSize: '10px',
                  fontWeight: 700,
                  lineHeight: 1,
                  marginTop: '4px',
                  textTransform: 'uppercase'
                }}>
                  {unit.label}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div style={{ background: '#ffe1de', height: '4px', width: '100%' }}>
        <div style={{
          background: '#cf1322',
          height: '100%',
          transition: 'width 0.3s ease',
          width: `${urgencyPercent}%`
        }} />
      </div>
    </div>
  );
};

export default QuickActionTimer;
